import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { secureGet, securePost } from '../../config/api';
import { formatDateTime, formatAmount, formatNumber, formatAmountClient, formatNumberDigit } from '../../utils/format';
import { PrimaryButton } from '../../components/PrimaryButton';
import Input from '../../components/Input';
import Notes from './Notes';
import OrderDetailsPopup from './OrderDetailsPopup';
import ImpayeDetailsPopup from './ImpayeDetailsPopup';
import ClientMap from "./ClientMap";
import PageScroller from '../../components/PageScroller';
import Breadcrumbs from '../../components/Breadcrumbs';

import EditIcon from '../../icons/editB.svg';
import NoteIcon from '../../icons/messagesquare.svg';
import InfoIcon from '../../icons/info.svg';

const ClientDetails = () => {
  const history = useHistory();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('infosGenerales');
  const [activeTab2, setActiveTab2] = useState("commandes");
  const [allFields, setAllFields] = useState([]);
  const [financialInfo, setFinancialInfo] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [Payments, setPayments] = useState([]);
  const [notesPopupVisible, setNotesPopupVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedImpaye, setSelectedImpaye] = useState(null);
  const { t } = useTranslation();
  const { id } = useParams();
  const [client, setClient] = useState({
    name: '',
    code: '',
    cityName: '',
    address: '',
    taxId: '',
    contactPrincipal: '',
    phone: '',
  });
  const [orderPage, setOrderPage] = useState(0);
  const [orderTotalPages, setOrderTotalPages] = useState(1);
  const [orderPageSize, setOrderPageSize] = useState(10);

  const [paymentPage, setPaymentPage] = useState(0);
  const [paymentTotalPages, setPaymentTotalPages] = useState(1);
  const [paymentPageSize, setPaymentPageSize] = useState(10);

  const [orderDateRange, setOrderDateRange] = useState({ startDate: "", endDate: "" });
  const [paymentDateRange, setPaymentDateRange] = useState({ startDate: "", endDate: "" });
  const [paymentStatusFilter, setPaymentStatusFilter] = useState(null);

  const [paymentSummary, setPaymentSummary] = useState({});
  const [requestBody, setRequestBody] = useState({});

  const STATUS_COLORS = {
    CREATED: "#0283F4",
    UNDER_APPROVAL: "#A8ADC3",
    VALIDATED: "#97DFFC",
    DELIVERED: "#06D6A0",
    DECLINED: "#EF476F",
    CANCELLED: "#38485C",
    EN_ATTENTE: "#FEA26F",
  };

  const STATE_COLORS = {
    true: "#EF476F",
    false: "#06D6A0",
  };

  const getStatusLabel = (status) => t(`order_status.${status}`) || status;

  const getStateLabel = (state) => t(`impaye_status.${state}`) || state;

  useEffect(() => {
    const abortController = new AbortController();
    const showSnackbar = () => {};
    const fetchClient = async () => {
      setLoading(true);
      try {
        const response = await secureGet(`/clients/${id}`, showSnackbar, {
          signal: abortController.signal
        });
        setClient(response.data);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Failed to fetch client:", error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchClient();

    return () => {
      abortController.abort();
    };
  }, [id]);

  useEffect(() => {
    const abortController = new AbortController();
    const showSnackbar = () => {};
    const fetchFields = async () => {
      try {
        const response = await secureGet("/config/client/fields", showSnackbar, {
          signal: abortController.signal
        });
        setAllFields(response.data);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Failed to fetch fields:", error);
        }
      }
    };
    fetchFields();

    return () => {
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    if (!client.id) return;

    const abortController = new AbortController();
    const showSnackbar = () => {};
    const fetchFinancialInfo = async () => {
      try {
        const response = await secureGet(`/clients/${client.id}/financials`, showSnackbar, {
          signal: abortController.signal
        });
        setFinancialInfo(response.data);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Failed to fetch financial info:", error);
        }
      }
    };
    fetchFinancialInfo();

    return () => {
      abortController.abort();
    };
  }, [client.id]);

  const fetchAllOrdersData = useCallback(async (page, size, signal) => {
    try {
      setLoading(true);

      const requestBody = {
        clientId: client.id,
        startDate: orderDateRange.startDate || null,
        endDate: orderDateRange.endDate || null,
      };

      const showSnackbar = () => {};
      const response = await securePost(
        `/carts/all/paged?page=${page}&size=${size}`,
        requestBody,
        showSnackbar,
        { signal }
      );

      const { content, totalPages } = response.data;
      setOrders(content);
      setOrderTotalPages(totalPages);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Error fetching all orders:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [orderDateRange, client.id]);

  useEffect(() => {
    if (!client.id) return;
    
    const abortController = new AbortController();
    fetchAllOrdersData(orderPage, orderPageSize, abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [client.id, orderPage, orderPageSize, orderDateRange, fetchAllOrdersData]);

  const fetchPayments = useCallback(async (page, size, signal) => {
    try {
      setLoading(true);

      const requestBody = {
        clientId: client.id,
        startDate: paymentDateRange.startDate || null,
        endDate: paymentDateRange.endDate || null,
        impaye: paymentStatusFilter || null,
      };

      setRequestBody(requestBody);

      const showSnackbar = () => {};
      const response = await securePost(
        `/payments/paged?page=${page}&size=${size}`,
        requestBody,
        showSnackbar,
        { signal }
      );

      const { content, totalPages } = response.data;
      setPayments(content);
      setPaymentTotalPages(totalPages);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Error fetching payments:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [paymentDateRange, paymentStatusFilter, client.id]);

  useEffect(() => {
    if (!client.id) return;
    
    const abortController = new AbortController();
    fetchPayments(paymentPage, paymentPageSize, abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [client.id, paymentPage, paymentPageSize, paymentDateRange, paymentStatusFilter, fetchPayments]);

  const fetchPaymentSummary = useCallback(async (signal) => {
    try {
      const showSnackbar = () => {};
      const response = await securePost(
        `/payments/summary`,
        requestBody,
        showSnackbar,
        { signal }
      );
      setPaymentSummary(response.data);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Payment Summary Error:", error);
      }
    }
  }, [requestBody]);

  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    if (requestBody.clientId) {
      fetchPaymentSummary(signal);
    }

    return () => {
      abortController.abort();
    };
  }, [fetchPaymentSummary, requestBody]);

  const handleOrderPageChange = (page) => {
    setOrderPage(page);
  };

  const handleOrderPageSizeChange = (size) => {
    setOrderPageSize(size);
    setOrderPage(0);
  };

  const handlePaymentPageChange = (page) => {
    setPaymentPage(page);
  };

  const handlePaymentPageSizeChange = (size) => {
    setPaymentPageSize(size);
    setPaymentPage(0);
  };

  const { name, code, cityName, address, taxId, contactPrincipal, phone } = client;

  const getAdditionalFields = () => {
    const excludedFields = [
      "Natures de pièce de règlement autorisées",
      "Encours échu non réglé",
      "Encours maximum",
      "Echéance autorisée"
    ];

    return allFields
      .map((field) => {
        const clientField = client.fields?.find((f) => f.name === field.name);
        return (
          <Input
            key={field.name}
            label={field.name}
            value={clientField ? clientField.value : ""}
            onChange={() => { }}
            disabled={true}
          />
        );
      });
  };
  const paymentField = client.pieceReglementAutorisee;

  const handleModifyClick = () => {
    history.push({
      pathname: `/clients/details/modify/${client.id}`,
      state: { initialValues: client, filters: location.state?.filters || {}, page: location.state?.page || 0, size: location.state?.size || 30 },
    });
  };

  const handleOrderDateRangeChange = (range) => {
    setOrderDateRange({
      startDate: range.startDate || "",
      endDate: range.endDate || "",
    });
  };

  const handlePaymentDateRangeChange = (range) => {
    setPaymentDateRange({
      startDate: range.startDate || "",
      endDate: range.endDate || "",
    });
  };


  const handlePaymentStatusChange = (status) => {
    setPaymentStatusFilter(status);
  };


  const handleNotesPopup = () => {
    setNotesPopupVisible(true);
  };

  const handleOrdersPopup = (order) => setSelectedOrder(order);

  const handleImpayePopup = (payment) => setSelectedImpaye(payment);

  if (!client) {
    return <p>Client not found</p>;
  }

  return (
    <div>
      {/* Client Details */}
      <Helmet>
        <title>GrayFox - {t("titles.client_details")}</title>
      </Helmet>
      <div className="flex flex-col mb-[20px]">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <Breadcrumbs />
            <h1 className="text-[32px] font-semibold">{t(`titles.client_details`)}</h1>
          </div>
          <div className="ml-auto text-[14px]">
            <PrimaryButton label={t(`primaryButtons.modify`)} icon={EditIcon} onClick={handleModifyClick} />
          </div>
        </div>

        <div className="flex items-center space-x-2 ">
          <p className="text-[24px] font-semibold text-[#6A78F8]">
            {name}
          </p>
          <div
            className="w-8 h-8 bg-white shadow-lg rounded-[12px] flex items-center justify-center relative"
            style={{ boxShadow: "0px 6px 26px rgba(98, 107, 125, 0.07)" }}
            onClick={handleNotesPopup}
            role="button"
            tabIndex="0"
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleNotesPopup();
              }
            }}          >
            <img src={NoteIcon} alt="Icon" className="w-5 h-5 text-[#6A78F8]" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-gray-200 rounded-[12px] p-1 w-fit">
        <button
          className={`px-12 py-3 text-[14px] font-bold rounded-[12px] ${activeTab === 'infosGenerales'
            ? 'bg-white text-[#043F88] shadow'
            : 'text-[#ACAFC6]'
            }`}
          onClick={() => setActiveTab('infosGenerales')}
        >
          {t(`titles.general_infos`)}
        </button>
        <button
          className={`px-12 py-3 text-[14px] font-bold rounded-[12px] ${activeTab === 'infosFinanciers'
            ? 'bg-white text-[#043F88] shadow'
            : 'text-[#ACAFC6]'
            }`}
          onClick={() => setActiveTab('infosFinanciers')}
        >
          {t(`titles.financial_infos`)}
        </button>
      </div>

      {/* Info Generales Content */}
      {activeTab === "infosGenerales" && (
        <div className="flex gap-4 mt-6">
          <div className="w-[75%]">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <Input label={t(`clientDetails.name`)} value={name} onChange={() => { }} disabled={true} />
              <Input label={t(`clientDetails.code`)} value={code} onChange={() => { }} disabled={true} />
              <Input label={t(`clientDetails.city`)} value={cityName} onChange={() => { }} disabled={true} />
              <Input label={t(`clientDetails.address`)} value={address || ""} onChange={() => { }} disabled={true} />
              <Input label={t(`clientDetails.taxId`)} value={taxId || ''} onChange={() => { }} disabled={true} />
              <Input label={t(`fields.clients.contactPrincipal`)} value={contactPrincipal || ''} onChange={() => { }} disabled={true} />
              <Input label={t(`fields.clients.phone`)} value={phone || ''} onChange={() => { }} disabled={true} />

            </div>

            <h3 className="text-[16px] font-medium text-[#ACAFC6] mt-8 mb-4">{t(`fields_types.additional`)}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {getAdditionalFields()}
            </div>
          </div>

          <div className="w-[25%]">
            <h3 className="text-[16px] font-medium text-[#ACAFC6]  mb-4">
              {t(`fields_types.clientLocation`)}
            </h3>
            <div className="bg-white shadow rounded-[12px] p-4 relative">
              <div className="bg-[#F5F6FB] rounded-[12px] overflow-hidden">
                <ClientMap latitude={client.latitude} longitude={client.longitude} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Financiers Content */}
      {activeTab === 'infosFinanciers' && (
        <div className="mt-6 ">
          {/* Payment Fields and Echu Total */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center flex-wrap gap-2">
              <h3 className="text-[16px] font-medium text-[#043F88] ">
                {t(`clientDetails.paymentAutorized`)}:
              </h3>
              <div className="flex flex-wrap gap-1">
                {paymentField ? (
                  paymentField.split(";").filter((piece) => piece.trim() !== "")
                    .map((piece, index) => (
                      <span
                        key={index}
                        className="bg-[#06D6A0] text-white font-semibold text-[12px] px-2 py-1"
                        style={{
                          background: "#06D6A0 0% 0% no-repeat padding-box",
                          borderRadius: "4px",
                          opacity: 1,
                        }}
                      >
                        {piece.trim()}
                      </span>
                    ))
                ) : (
                  <p className="text-gray-500 text-sm"></p>
                )}
              </div>
            </div>
            <div>
              <p className="text-[16px] font-medium text-[#043F88]">
                {t(`clientDetails.echuesCount`)}:{" "}
                <span
                  className="bg-[#06D6A0] text-white font-medium text-sm px-2 py-1 rounded-[4px]"
                  style={{
                    background: "#06D6A0 0% 0% no-repeat padding-box",
                    borderRadius: "4px",
                    opacity: 1,
                  }}
                >
                  {financialInfo?.echuesTotal}
                </span>
              </p>
            </div>
          </div>

          {/* Financial Info */}
          <div className="mt-6 grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            <div className="bg-white rounded-[12px]  relative flex flex-col justify-center min-h-[90px]" style={{ boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)" }}>
              <div className="absolute left-0 h-[55px] w-[5px] rounded-r-md bg-[#6B78F8]"></div>
              <h4 className="text-[#707070] text-[10px] font-semibold ml-6">{t(`clientDetails.maxEncours`)}</h4>
              <p className="font-bold text-[24px] ml-6 text-[#6B78F8]">
                {formatAmountClient(client.encoursMaximum || "0,00")} DHs
              </p>
            </div>

            {financialInfo && [
              { key: "echuesNumber", title: t(`clientDetails.maxEncours`), unit: " Jours", color: "#FEA26F", round: false },
              { key: "averageEcheance", title: t(`clientDetails.averageEcheance`), unit: " Jours", color: "#FFD166", round: true },
              { key: "averagePaymentReception", title: t(`clientDetails.averagePaymentReception`), unit: " Jours", color: "#06D6A0", round: true },
              { key: "nonRegleEchu", title: t(`clientDetails.nonRegleEchu`), unit: " DHs", color: "#3F3456", round: false },
              { key: "nonRegleNonEchu", title: t(`clientDetails.nonRegleNonEchu`), unit: " DHs", color: "#043F88", round: false },
              { key: "encours", title: t(`clientDetails.encours`), unit: " DHs", color: "#0283F4", round: false },
              { key: "impayes", title: t(`clientDetails.impayes`), unit: " DHs", color: "#FF6B6B", round: false },
            ].map(({ key, title, unit, color, round }) => (
              <div
                key={key}
                className="bg-white rounded-[12px] relative flex flex-col justify-center min-h-[90px] "
                style={{ boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)" }}
              >
                <div className="absolute left-0 h-[55px] w-[5px] rounded-r-md" style={{ backgroundColor: color }}></div>
                <h4 className="text-[#707070] text-[10px] font-semibold ml-6">{title}</h4>
                <p className="font-bold text-[24px] ml-6" style={{ color }}>
                  {financialInfo[key] !== undefined
                    ? unit === "Dhs"
                      ? formatAmountClient(financialInfo[key])
                      : `${round ? formatNumberDigit(financialInfo[key]) : formatNumber(financialInfo[key])}${unit}`
                    : `0${round ? ".00" : ""}${unit}`}
                </p>
              </div>
            ))}
          </div>

          <div className="flex justify-end items-center mt-4 gap-4">
            <div className="flex justify-between items-left mt-4 w-[57.2%]">
              {/* Left-aligned total only for payments */}
              {activeTab2 === "payments" && (
                <div className="flex-shrink-0 min-w-[200px] mr-4 mt-3">
                  <span className="font-bold text-[#043F88] text-[16px] whitespace-nowrap">
                    TOTAL:
                    <span className="text-[#043F88] text-[16px] ml-2">
                      {formatAmount(paymentSummary.totalAmount)}
                    </span>
                  </span>
                </div>
              )}

              {/* Filters container */}
              <div className="flex items-center gap-4 w-full">
                {/* Orders Date Filter */}
                {activeTab2 === "commandes" && (
                  <div className="flex items-center gap-4 ml-auto">
                    <button
                      className="text-blue-600 text-sm font-medium cursor-pointer hover:underline"
                      onClick={() => {
                        setOrderDateRange({ startDate: "", endDate: "" });
                        fetchAllOrdersData(orderPage, orderPageSize);
                      }}
                    >
                      {t(`actions.reset`)}
                    </button>

                    <Input
                      type="daterange"
                      value={orderDateRange}
                      onChange={handleOrderDateRangeChange}
                    />
                  </div>
                )}

                {/* Payments Date Filter */}
                {activeTab2 === "payments" && (
                  <div className="flex items-center gap-4 ml-auto">
                    <button
                      className="text-blue-600 text-sm font-medium cursor-pointer hover:underline"
                      onClick={() => {
                        setPaymentDateRange({ startDate: "", endDate: "" });
                        setPaymentStatusFilter(null);
                        fetchPayments(paymentPage, paymentPageSize);
                      }}
                    >
                      {t(`actions.reset`)}
                    </button>

                    <Input
                      type="daterange"
                      value={paymentDateRange}
                      onChange={handlePaymentDateRangeChange}
                    />

                    <Input
                      type="dropdown"
                      label={t("filters.payments.status")}
                      value={paymentStatusFilter || ""}
                      options={[
                        { label: t("impaye_status.true"), value: "true" },
                        { label: t("impaye_status.false"), value: "false" },
                      ]}
                      onChange={(selected) => handlePaymentStatusChange(selected)}
                      context="filter"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Second set of tabs */}
            <div className="flex justify-end bg-gray-200 rounded-[12px] p-1 w-fit mt-4">
              <button
                className={`px-6 py-2 text-[14px] font-bold rounded-[12px] ${activeTab2 === "commandes"
                  ? "bg-[#043F88] text-white shadow-md"
                  : "bg-transparent text-[#ACAFC6]"
                  }`}
                onClick={() => setActiveTab2("commandes")}
              >
                {t(`titles.client_orders`)}
              </button>
              <button
                className={`px-6 py-2 text-[14px] font-bold rounded-[12px] ${activeTab2 === "payments"
                  ? "bg-[#043F88] text-white shadow-md"
                  : "bg-transparent text-[#ACAFC6]"
                  }`}
                onClick={() => setActiveTab2("payments")}
              >
                {t(`titles.client_payments`)}
              </button>
            </div>
          </div>

          {/* Historique des Commandes content */}
          {activeTab2 === "commandes" && (
            <div className="mt-2 flex flex-col gap-4">
              {orders.map((order, index) => (
                <div
                  key={index}
                  className="bg-white py-4 px-12 rounded-[12px] grid grid-cols-12 items-center"
                  style={{ boxShadow: "0px 6px 26px rgba(98, 107, 125, 0.07)" }}
                >
                  <div className="col-span-3 flex flex-col items-start ">
                    <span
                      className="text-white text-[9px] font-semibold px-1 py-0 rounded-[12px]"
                      style={{ backgroundColor: STATUS_COLORS[order.status] || "#A8ADC3" }}
                    >
                      {getStatusLabel(order.status)}
                    </span>
                    <p className="text-[#043F88] text-[16px] font-semibold mt-1">
                      {order.cartNumber}
                    </p>
                  </div>
                  <div className="col-span-3 flex flex-col items-start">
                    <p className="text-[#707070] text-[12px] font-semibold">{t(`rows.createdAt`)}</p>
                    <p className="text-[#043F88] text-[16px] font-semibold">
                      {formatDateTime(order.createdAt)}
                    </p>
                  </div>
                  <div className="col-span-2 flex flex-col items-start">
                    <p className="text-[#707070] text-[12px] font-semibold">TOTAL HT</p>
                    <p className="text-[#043F88] text-[16px] font-semibold">
                      {formatAmount(order.priceHT)}
                    </p>
                  </div>
                  <div className="col-span-2 flex flex-col items-start">
                    <p className="text-[#707070] text-[12px] font-semibold">TOTAL TTC</p>
                    <p className="text-[#043F88] text-[16px] font-semibold">
                      {formatAmount(order.priceTTC)}
                    </p>
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <div
                      className="w-8 h-8 bg-[#F2F3F3]  rounded-[12px] flex items-center justify-center cursor-pointer"
                      style={{
                        border: "1px solid #0200404D",
                      }}
                      onClick={() => handleOrdersPopup(order)}
                    >
                      <img src={InfoIcon} alt="Info" className="w-4 h-4 text-[#043F88]" />
                    </div>
                  </div>
                </div>
              ))}
              <PageScroller
                pageNumber={orderPage}
                totalPages={orderTotalPages}
                pageSize={orderPageSize}
                onPageChange={handleOrderPageChange}
                onPageSizeChange={handleOrderPageSizeChange}
              />
            </div>
          )}

          {/* Historique des Impayés content */}
          {activeTab2 === "payments" && (
            <div className="mt-2 flex flex-col gap-4">
              {Payments.map((payment, index) => (
                <div
                  key={index}
                  className="bg-white py-4 px-12 rounded-[12px] grid grid-cols-12 items-center"
                  style={{ boxShadow: "0px 6px 26px rgba(98, 107, 125, 0.07)" }}
                >
                  {/* Status + Payment ID */}
                  <div className="col-span-2 flex flex-col items-start">
                    <span
                      className="text-white text-[9px] font-semibold px-1 py-0 rounded-[12px]"
                      style={{ backgroundColor: STATE_COLORS[payment.impaye] || "#A8ADC3" }}
                    >
                      {getStateLabel(payment.impaye)}
                    </span>
                    <p className="text-[#043F88] text-[16px] font-semibold mt-1">
                      {payment.reference}
                    </p>
                  </div>

                  {/* Creation Date */}
                  <div className="col-span-3 flex flex-col items-start">
                    <p className="text-[#707070] text-[12px] font-semibold">{t(`rows.createdAt`)}</p>
                    <p className="text-[#043F88] text-[16px] font-semibold">
                      {formatDateTime(payment.createdAt)}
                    </p>
                  </div>

                  {/* Payment Type */}
                  <div className="col-span-3 flex flex-col items-start">
                    <p className="text-[#707070] text-[12px] font-semibold">{t(`rows.paymentType`)}</p>
                    <p className="text-[#043F88] text-[16px] font-semibold truncate">
                      {payment.type?.name}
                    </p>
                  </div>

                  {/* Amount */}
                  <div className="col-span-2 flex flex-col items-start">
                    <p className="text-[#707070] text-[12px] font-semibold">{t(`rows.amount`)}</p>
                    <p className="text-[#043F88] text-[16px] font-semibold">
                      {formatAmount(payment.amount)}
                    </p>
                  </div>

                  {/* Info Button */}
                  <div className="col-span-1 flex justify-end">
                    <div
                      className="w-8 h-8 bg-[#F2F3F3] rounded-[12px] flex items-center justify-center cursor-pointer border border-[#0200404D]"
                      onClick={() => handleImpayePopup(payment)}
                    >
                      <img src={InfoIcon} alt="Info" className="w-4 h-4 text-[#043F88]" />
                    </div>
                  </div>
                </div>
              ))}
              <PageScroller
                pageNumber={paymentPage}
                totalPages={paymentTotalPages}
                pageSize={paymentPageSize}
                onPageChange={handlePaymentPageChange}
                onPageSizeChange={handlePaymentPageSizeChange}
              />
            </div>
          )}

          {selectedOrder && (
            <OrderDetailsPopup order={selectedOrder} onClose={() => setSelectedOrder(null)} />
          )}

          {selectedImpaye && (
            <ImpayeDetailsPopup payment={selectedImpaye} onClose={() => setSelectedImpaye(null)} />
          )}

        </div>
      )}

      {notesPopupVisible && (
        <Notes
          open={notesPopupVisible}
          onClose={() => setNotesPopupVisible(false)}
          clientId={client.id}
        />
      )}
    </div>
  );
};

export default ClientDetails;